import { useEffect, useMemo, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import { parseAddressList } from '@/lib/emailHtml'
import { useAsync } from '@/lib/useAsync'
import { Dialog } from '@/components/ui/dialog'
import { EmailEditor } from '@/components/ui/emaileditor'
import { Field, Input, Select } from '@/components/ui/form'
import { Button, cx } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import type { EmailPlaceholder, QuotationEmailDraft } from '@/types/api'

/**
 * The placeholder the outbox turns into the customer's signing link.
 *
 * Named here rather than typed into a string twice, because the value is a contract with
 * the API: the handler mints a link when it finds this and does nothing when it does not.
 */
const QUOTATION_LINK = 'QuotationLink'

/** How the placeholder is written into the body. */
const QUOTATION_LINK_TOKEN = `{{${QUOTATION_LINK}}}`

/**
 * The same match the API makes, whitespace inside the braces and case included.
 *
 * A stricter test here would refuse to send a body the API would have accepted, which is a
 * worse failure than the one it is preventing: the rep sees a control they cannot use and
 * no way to find out why.
 */
const QUOTATION_LINK_PATTERN = /\{\{\s*QuotationLink\s*\}\}/i

/**
 * Whether a body carries the signing link.
 *
 * Exported for the test: a rule that silently stops matching turns the Send button off with
 * no explanation a rep could act on, and that is not something to find out from a user.
 */
export function hasSigningLink(body: string): boolean {
  return QUOTATION_LINK_PATTERN.test(body)
}

/**
 * Sends the quotation, which is also how it becomes Sent.
 *
 * <p>
 * The draft arrives filled in from the country's default template: the contact's address,
 * the template's CC, and a body with every placeholder resolved except the signing link.
 * That one stays a placeholder on purpose - an acceptance token is readable once, at the
 * moment it is minted, and minting revokes the link the customer may already be holding, so
 * a dialog that resolved it to show you would kill a live link every time it was opened and
 * closed. It is minted as the mail goes.
 * </p>
 *
 * What is edited here is this one mail. The template is edited on its own screen, by
 * whoever is allowed to.
 */
export function SendQuotationDialog({
  quotationId,
  onClose,
  onSent,
}: {
  quotationId: string
  onClose: () => void
  onSent: () => void
}) {
  const toast = useToast()

  const [templateId, setTemplateId] = useState<string | undefined>()
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [sending, setSending] = useState(false)

  const draft = useAsync<QuotationEmailDraft>(
    (signal) =>
      api.get<QuotationEmailDraft>(
        `/quotations/${quotationId}/email-draft`,
        templateId ? { templateId } : undefined,
        signal,
      ),
    [quotationId, templateId],
  )

  const placeholders = useAsync<EmailPlaceholder[]>(
    (signal) => api.get<EmailPlaceholder[]>('/email-templates/placeholders', undefined, signal),
    [],
  )

  const loaded = draft.data

  // Written in whenever a draft arrives - the first load, and again on switching template.
  // Switching is a deliberate "start from that one instead", so it replaces the wording.
  useEffect(() => {
    if (!loaded) return

    setTo(loaded.to)
    setCc(loaded.cc)
    setSubject(loaded.subject)
    setBody(loaded.bodyHtml)
  }, [loaded])

  const toError = useMemo(() => {
    const parsed = parseAddressList(to)
    if (parsed.invalid) return `${parsed.invalid} is not an email address.`

    return parsed.addresses.length === 0 ? 'Say who this goes to.' : undefined
  }, [to])

  const ccError = useMemo(() => {
    const parsed = parseAddressList(cc)
    return parsed.invalid ? `${parsed.invalid} is not an email address.` : undefined
  }, [cc])

  // The outbox mints the signing link when it finds this placeholder, and nothing else
  // creates one. A mail without it reaches the customer with no way to accept, and leaves a
  // quotation marked Sent with no link behind it - so it is stopped here rather than at the
  // API, where the rep would have lost nothing but would have had to guess why.
  const linkMissing = !hasSigningLink(body)

  const canSend =
    !toError && !ccError && subject.trim().length > 0 && body.trim().length > 0 && !linkMissing

  async function send() {
    setSending(true)
    try {
      await api.post(`/quotations/${quotationId}/send-email`, {
        to,
        cc,
        subject: subject.trim(),
        bodyHtml: body,
      })

      // Queued, not delivered. Saying "Sent" here would be a claim about a mail host that
      // has not answered yet; the outbox retries, and a failure raises a notification.
      toast.show('Queued for delivery. The quotation is now Sent.', 'success')
      onSent()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.message : 'Could not send it', 'error')
    } finally {
      setSending(false)
    }
  }

  return (
    <Dialog
      open
      wide
      title={loaded?.quotationNo ? `Send ${loaded.quotationNo}` : 'Send quotation'}
      description="The customer receives this message, the quotation document, and their own signing link."
      onClose={onClose}
      loading={draft.loading && !loaded}
      error={draft.error ?? undefined}
      onRetry={draft.reload}
      footer={
        <>
          <span className={cx('mr-auto text-[12.5px]', linkMissing ? 'text-crit' : 'text-muted')}>
            {linkMissing
              ? `This message has no signing link. Insert ${QUOTATION_LINK_TOKEN} so the customer has something to open.`
              : 'The customer reads and signs through the link in this message.'}
          </span>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={sending} disabled={!canSend} onClick={() => void send()}>
            Send and mark as sent
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {loaded && loaded.templates.length > 1 && (
          <Field label="Template" hint="Switching replaces the subject and message below.">
            {(id) => (
              <Select
                id={id}
                value={templateId ?? loaded.templateId ?? ''}
                onChange={(event) => setTemplateId(event.target.value)}
              >
                {loaded.templates.map((choice) => (
                  <option key={choice.id} value={choice.id}>
                    {choice.name}
                    {choice.isDefault ? ' (default)' : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        {loaded && loaded.templates.length === 0 && (
          <p className="rounded-md bg-wash px-3 py-2 text-[12.5px] text-muted">
            This country has no email template yet, so nothing is filled in. Write the mail here,
            or ask for a template to be added under Master data.
          </p>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="To" required error={toError} hint="Separate several with a semicolon.">
            {(id) => (
              <Input id={id} value={to} onChange={(event) => setTo(event.target.value)} />
            )}
          </Field>

          <Field label="CC" error={ccError}>
            {(id) => (
              <Input id={id} value={cc} onChange={(event) => setCc(event.target.value)} />
            )}
          </Field>
        </div>

        <Field label="Subject" required>
          {(id) => (
            <Input id={id} value={subject} onChange={(event) => setSubject(event.target.value)} />
          )}
        </Field>

        <Field label="Message">
          {(id) => (
            <EmailEditor
              id={id}
              value={body}
              placeholders={placeholders.data ?? []}
              onChange={setBody}
              placeholder="What the customer will read"
            />
          )}
        </Field>

        <p className="text-[12.5px] text-muted">
          The link in this message is created as the mail goes out, and it replaces any signing
          link created before it - so the copy the customer is holding is always the newest one.
        </p>
      </div>
    </Dialog>
  )
}
